import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { cn } from '@/lib/utils'
import type { ReactElement, ReactNode } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { MermaidRenderer } from './MermaidRenderer'

const markdownComponents = {
  pre({ children, ...props }: React.ComponentPropsWithoutRef<'pre'>) {
    const codeElement = children as ReactElement
    if (codeElement?.props?.className?.includes('language-mermaid')) {
      return <MermaidRenderer chart={String(codeElement.props.children).replace(/\n$/, '')} />
    }
    return <pre {...props}>{children}</pre>
  },
  code({ className, children, ...props }: React.ComponentPropsWithoutRef<'code'>) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    )
  },
}

/**
 * Hover card that renders `documentation` as markdown (GFM + mermaid), matching form field documentation.
 */
export function MarkdownDocumentationHover({
  documentation,
  children,
  contentClassName,
}: {
  documentation: string
  children: ReactNode
  contentClassName?: string
}) {
  return (
    <HoverCard openDelay={10} closeDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className={cn('w-auto max-w-4xl', contentClassName)}>
        <div
          className={cn(
            'prose prose-sm max-w-none gap-10',
            // Default `prose` body color is meant for light surfaces; without these, dark popovers get low-contrast gray-on-gray.
            'dark:prose-invert',
            'dark:prose-headings:text-foreground',
            'dark:prose-p:text-foreground',
            'dark:prose-li:text-foreground',
            'dark:prose-strong:text-foreground',
            'dark:prose-code:text-foreground',
            'dark:prose-pre:text-foreground',
            'dark:prose-th:text-foreground',
            'dark:prose-td:text-foreground',
            'dark:prose-blockquote:text-foreground'
          )}
        >
          <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {documentation}
          </Markdown>
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
